/**
 * The offer and the risk disclosure, accepted before the cabinet is used.
 *
 * Shown over every page of an account until the versions in force are
 * accepted — at first sign-in and again whenever a text changes. The client
 * accepts exactly the versions shown; the server records them with their text's
 * digest (owner decision 2026-10-13).
 */
import { useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useId, useState } from "react";

import { Button } from "@/design-system/Button";
import { acceptLegalDocuments } from "@/shared/api";
import { LEGAL_KEY, useLegalDocuments } from "@/shared/legal/legal";

export function LegalGate(): React.JSX.Element | null {
  const query = useLegalDocuments();
  const queries = useQueryClient();
  const titleId = useId();
  const [agreed, setAgreed] = useState(false);
  const [pending, setPending] = useState(false);
  const [refused, setRefused] = useState(false);
  const data = query.data;
  if (data === undefined || !data.acceptance_required) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="legal-gate"
        className="flex max-h-[90dvh] w-full max-w-2xl flex-col rounded-lg border border-border-subtle bg-background-elevated shadow-xl"
      >
        <h2 id={titleId} className="flex items-center gap-2 border-b border-border-subtle px-5 py-4 text-body-lg font-semibold text-text-primary">
          <FileText aria-hidden="true" className="size-5 text-action-primary" />
          Условия использования Skryptos
        </h2>
        <div className="flex flex-col gap-6 overflow-y-auto px-5 py-4" tabIndex={0}>
          {data.documents.map((document) => (
            <section key={document.kind} className="flex flex-col gap-2">
              <h3 className="text-body-main font-semibold text-text-primary">{document.title}</h3>
              <p className="text-helper-sm text-text-muted">Редакция от {document.version}</p>
              <div className="whitespace-pre-line text-body-main text-text-secondary">{document.body}</div>
            </section>
          ))}
        </div>
        <form
          className="flex flex-col gap-3 border-t border-border-subtle px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!agreed || pending) return;
            setPending(true);
            setRefused(false);
            void acceptLegalDocuments({
              body: { documents: Object.fromEntries(data.documents.map((document) => [document.kind, document.version])) },
            })
              .then(async (result) => {
                if (result.response?.ok !== true) {
                  // A changed version reads afresh; anything else is said.
                  setRefused(true);
                }
                await queries.invalidateQueries({ queryKey: LEGAL_KEY });
              })
              .catch(() => { setRefused(true); })
              .finally(() => { setPending(false); });
          }}
        >
          <label className="flex cursor-pointer items-start gap-2 text-body-main text-text-primary">
            <input
              type="checkbox"
              className="mt-1 size-4 cursor-pointer"
              checked={agreed}
              onChange={(event) => { setAgreed(event.target.checked); }}
            />
            <span>Я прочитал(а) пользовательское соглашение и уведомление о рисках, понимаю риски и принимаю условия полностью.</span>
          </label>
          {refused && <p role="alert" className="text-sm text-status-warning">Не удалось сохранить согласие. Обновите страницу и повторите.</p>}
          <div>
            <Button type="submit" variant="primary" disabled={!agreed || pending}>
              {pending ? "Сохраняю…" : "Принимаю"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
